import {
  Box,
  getColorByPath,
  Grid,
  GridItem,
  gridMinmax,
  gridTracks,
  Heading,
  HStack,
  Integer,
  Show,
  Text,
  ThemeColorPath,
  ValueByDevice,
  VStack,
} from 'platform/foundation';
import styled, {useTheme} from 'styled-components';
import {Pattern, match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {
  RecordAuditTask,
  RecordAuditResultValue,
  SubmitRecordAuditResultRequestBody,
  useSubmitRecordAuditResultMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';

import {ApiError} from 'shared/web';

import {handleApiError} from '../../utils/handleApiError';
import {AutosaveIndicator, AutosaveStatus} from './AutosaveIndicator';
import {OkBadNaRadio} from './OkBadNaRadio';
import {RecordAuditTaskAttachments} from './RecordAuditTaskAttachments';
import {RecordAuditTaskNote} from './RecordAuditTaskNote';
import {STRIPE_WIDTH} from './STRIPE_WIDTH';
import {TASK_CARD_CONTENT_PADDING} from './TASK_CARD_CONTENT_PADDING';
import {TaskRequirementChip} from './TaskRequirementChip';

// Left color stripe per result; unanswered tasks get a neutral grey one.
const NEUTRAL_STRIPE_COLOR: ThemeColorPath = 'palettes.neutral.40.100';
const STRIPE_COLOR_BY_VALUE: Record<RecordAuditResultValue, ThemeColorPath> = {
  OK: 'palettes.green.60.100',
  BAD: 'palettes.red.60.100',
  // Darker grey than the unanswered stripe so the N/A wash is actually visible.
  NA: 'palettes.neutral.70.100',
};
// Wash gradient: visible-but-gentle leading edge that eases out smoothly. Hex alphas
// (~35% → ~12% → 0) and band widths (steps) tune intensity and spread.
const WASH_START_ALPHA = '1F';
const WASH_MID_ALPHA = '0A';
const WASH_MID_STEPS = 3;
const WASH_FADE_STEPS = 8;

// Subtle result-colour wash next to the crisp solid stripe: a soft glow that fades out
// over a narrow band and is transparent across the rest of the card.
// Raw styled.div (not platform Box): Box exposes no `background`/`linear-gradient` prop,
// so this colour wash cannot be expressed with the design system.
const ContentWash = styled.div<{$color: string}>`
  flex: 1;
  min-width: 0;
  background: linear-gradient(
    to right,
    ${({$color}) => `${$color}${WASH_START_ALPHA}`} 0,
    ${({$color, theme}) => `${$color}${WASH_MID_ALPHA} ${theme.getSize(WASH_MID_STEPS)}`},
    transparent ${({theme}) => theme.getSize(WASH_FADE_STEPS)}
  );
`;

// Two-column grid: the number badge sizes to content and vertically centers against
// the title row, while the title and body share the right column (body lines up under
// the title). Grid handles the widths, so no padding/margin offsets to drift.
const TASK_COLUMNS = gridTracks('auto', gridMinmax(0, '1fr'));
// Body shares the title's column (second) and flows to the next row. On smaller screens
// (mobile, tablet) the body spans both columns so it fills the otherwise-empty space under the
// number badge instead of leaving a dead gap.
const BODY_COLUMN_START: ValueByDevice<number> = [1, 1, 2];
const BODY_COLUMN_SPAN: ValueByDevice<Integer> = [2, 2, 1];

interface RecordAuditTaskCardProps {
  auditId: string;
  task: RecordAuditTask;
  // Pre-formatted "section.task" label, e.g. "1.1".
  number: string;
  isReadonly?: boolean;
}

export function RecordAuditTaskCard(props: RecordAuditTaskCardProps) {
  const [submitResult, submitResultState] = useSubmitRecordAuditResultMutation();

  const value = props.task.value;
  const note = props.task.note;

  // Autosave feedback is transient only: a blue "Saving…" while a result is in flight and a red
  // "Error" on failure. A successful save shows nothing — the answered radio is the persisted truth,
  // so a permanent "Saved" badge would just be redundant noise on every filled task.
  const status: AutosaveStatus = match(submitResultState)
    .when(
      (s) => s.isLoading,
      () => 'saving' as const
    )
    .when(
      (s) => s.isError,
      () => 'error' as const
    )
    .otherwise(() => 'pristine' as const);

  const submit = (body: SubmitRecordAuditResultRequestBody) => {
    submitResult({
      auditId: props.auditId,
      originalTaskDefinitionId: props.task.originalTaskDefinitionId,
      body,
    })
      .unwrap()
      .catch((error: ApiError) => handleApiError(error));
  };

  const theme = useTheme();
  const stripeColor: ThemeColorPath = match(value)
    .with(Pattern.nullish, () => NEUTRAL_STRIPE_COLOR)
    .otherwise((resultValue) => STRIPE_COLOR_BY_VALUE[resultValue]);

  return (
    <Box
      borderRadius="small"
      overflow="hidden"
      border="1px solid"
      borderColor="palettes.neutral.40.100"
      backgroundColor="palettes.white.10.100"
    >
      <HStack spacing={0} align="stretch">
        <Box width={STRIPE_WIDTH} backgroundColor={stripeColor} />
        <ContentWash $color={getColorByPath(stripeColor, theme) ?? 'transparent'}>
          <Box padding={TASK_CARD_CONTENT_PADDING}>
            <Grid columns={TASK_COLUMNS} spacing={2} align="center">
              {/* Number cell (auto width) vertically centers against the title row. */}
              <Text size="small" color="tertiary" alternative>
                {props.number}
              </Text>
              {/* Wrap so the requirement chips + weight drop below the title on narrow
                widths instead of overflowing and clipping the card. */}
              <HStack justify="space-between" align="center" spacing={3} wrap>
                <HStack spacing={3} align="center">
                  <Heading size={4}>{props.task.text}</Heading>
                  {/* Autosave feedback sits right after the task title. No autosaving happens on a
                    closed (readonly) audit, so it is hidden there. */}
                  <Show when={!props.isReadonly}>
                    <AutosaveIndicator status={status} />
                  </Show>
                </HStack>
                <HStack spacing={3} align="center">
                  <Show when={props.task.isMandatory}>
                    <TaskRequirementChip
                      variant="mandatory"
                      label={i18n.t('page.recordAudit.detail.mandatory')}
                    />
                  </Show>
                  <Show when={props.task.fileRequired}>
                    <TaskRequirementChip
                      variant="fileRequired"
                      label={i18n.t('page.recordAudit.detail.fileRequired')}
                    />
                  </Show>
                  <WeightDots weight={props.task.weight} />
                </HStack>
              </HStack>
              {/* Body shares the title column and flows under it. */}
              <GridItem columnStart={BODY_COLUMN_START} span={BODY_COLUMN_SPAN}>
                <VStack spacing={2}>
                  <OkBadNaRadio
                    value={value}
                    onChange={(next) => submit({value: next})}
                    isReadonly={props.isReadonly}
                    allowNa={!props.task.isMandatory}
                  />
                  {/* Note + attachments stay collapsed until the task is answered, so an
                    untouched checklist reads as a clean list of questions. */}
                  <Show when={isNotNil(value)}>
                    <RecordAuditTaskNote
                      value={note}
                      onChange={(next) => submit({note: next})}
                      isDisabled={props.isReadonly}
                    />
                    <RecordAuditTaskAttachments
                      taskId={props.task.originalTaskDefinitionId}
                      initialFiles={props.task.files}
                      onChange={(fileIds) => submit({fileIds})}
                      isDisabled={props.isReadonly}
                    />
                  </Show>
                </VStack>
              </GridItem>
            </Grid>
          </Box>
        </ContentWash>
      </HStack>
    </Box>
  );
}

const WEIGHT_SCALE = [1, 2, 3, 4, 5] as const;
const WEIGHT_DOT_SIZE = '0.375rem';
const DOT_ON_COLOR: ThemeColorPath = 'palettes.blue.60.100';
const DOT_OFF_COLOR: ThemeColorPath = 'palettes.neutral.40.100';

function WeightDots(props: {weight: number}) {
  return (
    <HStack spacing={2} align="center">
      <Text size="xSmall" color="tertiary">
        {i18n.t('entity.recordAudit.task.weight').toUpperCase()}
      </Text>
      <HStack spacing={1} align="center">
        {WEIGHT_SCALE.map((dot) => (
          <Box
            key={dot}
            width={WEIGHT_DOT_SIZE}
            height={WEIGHT_DOT_SIZE}
            borderRadius="circular"
            backgroundColor={dot <= props.weight ? DOT_ON_COLOR : DOT_OFF_COLOR}
          />
        ))}
      </HStack>
      <Text size="small" alternative>
        {props.weight}
      </Text>
    </HStack>
  );
}
